import React, { useState, useEffect, useRef } from "react";
import {
  FaSmile,
  FaMicrophone,
  FaTimes,
  FaFileAudio,
  FaFileVideo,
  FaFilePdf,
  FaFileWord,
  FaFileImage,
  FaFileAlt,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileCsv,
  FaFont,
  FaFile,
} from "react-icons/fa";
import { IoIosCloseCircle, IoMdAdd, IoMdClose } from "react-icons/io";
import { LuCalendarPlus2 } from "react-icons/lu";
import SchedulePopover from "../../../../Components/SchedulePopover/SchedulePopover";
import ScheduledMessagesList from "../../../../Components/SchedulePopover/ScheduledMessagesList";
import EmojiPicker from "emoji-picker-react";
import { MdSend } from "react-icons/md";
import "react-datepicker/dist/react-datepicker.css";
import clsx from "clsx";
import { BiTrash } from "react-icons/bi";
import { FiPlay, FiPause } from "react-icons/fi";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { HiOutlineMicrophone } from "react-icons/hi2";
import { TfiGallery } from "react-icons/tfi";
import { IoDocumentTextOutline, IoSendSharp } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { compressFile } from "../../../../utils/compressFile";
import { v4 as uuidv4 } from 'uuid';
import { encrypt } from "../../../../Components/EncryptDecryptMessage/EncryptDecryptMessage";
import { postApiCaller } from "../../../../Libs/apiCaller";
import { useTheme } from "../../../../Context/ThemeContext";

const getFileExtension = (url) => {
  return url?.split(".").pop().toLowerCase();
};

function categorizeFile(file) {
  if (!file || !file.type) return "document";

  const type = file.type;
  if (!type) return "document";

  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";
  if (type.startsWith("audio/")) return "audio";

  return "document";
}

const FileIcon = ({ url }) => {
  const ext = getFileExtension(url);

  const iconMap = {
    pdf: <FaFilePdf className="size-20 text-red-500" />,
    doc: <FaFileWord className="size-20 text-blue-600" />,
    docx: <FaFileWord className="size-20 text-blue-600" />,
    jpg: <FaFileImage className="size-20 text-green-500" />,
    png: <FaFileImage className="size-20 text-green-500" />,
    txt: <FaFileAlt className="size-20 text-gray-500" />,
    xls: <FaFileExcel className="size-20 text-green-600" />,
    xlsx: <FaFileExcel className="size-20 text-green-600" />,
    ppt: <FaFilePowerpoint className="size-20 text-orange-500" />,
    pptx: <FaFilePowerpoint className="size-20 text-orange-500" />,
    csv: <FaFileCsv className="size-20 text-blue-400" />,
    ttf: <FaFont className="size-20 text-purple-500" />,
    otf: <FaFont className="size-20 text-purple-500" />,
    ttc: <FaFont className="size-20 text-purple-500" />,
  };

  return iconMap[ext] || <FaFile className="size-20 text-gray-400" />;
};

const ffmpeg = new FFmpeg();

const BottomTypeArea = ({
  onScheduleClick,
  activeChat,
  sendMessage,
  sendFilesInMessage,
  showAddMenu,
  setShowAddMenu,
  socket,
  chatConversationId,
  userInfo,
  getSecretKey,
}) => {
  const { theme } = useTheme();
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showSchedulePopover, setShowSchedulePopover] = useState(false);
  const [showScheduledList, setShowScheduledList] = useState(false);
  const [preventSubmit, setPreventSubmit] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [stream, setStream] = useState(null);
  const addMenuRef = useRef(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [files, setFiles] = useState([]);
  const textareaRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const datePickerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const sourceRef = useRef(null);

  // Add state for previewing files
  const [previewFiles, setPreviewFiles] = useState([]);

  // Typing indicator refs
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const reEmitIntervalRef = useRef(null);
  const uid = localStorage.getItem("userId");

  useEffect(() => {
    if (location.pathname === "/home" && location?.state?.url && activeChat) {
      setMessage(
        `📢 Check out this channel!\n🔗 Name: ${location?.state?.name
        } \n\n📝 Description: ${location?.state?.description || "No description available."
        } \n👉 Join here: ${location?.state?.url} `
      );
      navigate(location.pathname, { replace: true });
    } else if (activeChat) {
      setMessage("");
    }
  }, [activeChat]);

  useEffect(() => {
    setPreviewFiles([]);
    // Clean up recording state and resources
    if (isRecording) {
      stopRecording();
    }
    setIsRecording(false);
    setIsPaused(false);
    clearInterval(timerRef.current);
    timerRef.current = null;
    audioChunksRef.current = [];
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current = null;
    }
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setRecordingTime(0);
  }, [activeChat]);

  // Close Emoji Picker when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
        setShowAddMenu(false);
      }
    };

    if (showEmojiPicker || showAddMenu) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showEmojiPicker]);

  // Close Date Picker when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showDatePicker]);

  const handleEmojiClick = (emojiObject) => {
    setMessage((prev) => prev + emojiObject.emoji);
  };

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      if (textarea.scrollHeight <= 160) {
        textarea.style.height = `${textarea.scrollHeight} px`;
        textarea.style.overflowY = "hidden";
      } else {
        textarea.style.height = `${160} px`;
        textarea.style.overflowY = "auto";
      }
    }
  }, [message]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && e.shiftKey) {
        setPreventSubmit(true);
      } else if (e.key === "Enter" && !e.shiftKey && previewFiles.length > 0) {
        if (preventSubmit) {
          e.preventDefault();
          return;
        }
        e.preventDefault(); // only Enter: submit
        handleSubmit();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewFiles]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
      setPreventSubmit(true);
    } else if (e.key === "Enter" && !e.shiftKey) {
      if (preventSubmit) {
        e.preventDefault();
        return;
      }
      e.preventDefault(); // only Enter: submit
      setMessage("");
      handleSubmit();
    }
  };

  const handleChange = (e) => {
    setMessage(e.target.value);
    setPreventSubmit(false); // reset prevent flag when message is edited

    // Typing indicator logic
    if (!socket || !chatConversationId || !uid) {
      return;
    }

    if (!chatConversationId) {
      return;
    }

    if (!uid) {
      if (socket && socket.emit) {
        socket.emit('userStoppedTyping', {
          conversationId: typeof chatConversationId === 'string'
            ? chatConversationId
            : chatConversationId?._id,
          userId: localStorage.getItem('userId')
        });
      }
      return;
    }

    const conversationId = typeof chatConversationId === 'string'
      ? chatConversationId
      : chatConversationId._id;

    if (!conversationId) {
      return;
    }

    // Emit typing event if not already typing
    if (!isTypingRef.current) {
      socket.emit('typing', {
        conversationId,
        userId: uid
      });
      isTypingRef.current = true;

      // Start re-emit interval ONLY when first starting to type
      // Re-emit typing every 2 seconds to prevent timeout on receiver
      reEmitIntervalRef.current = setInterval(() => {
        if (socket && conversationId && isTypingRef.current) {
          socket.emit('typing', {
            conversationId,
            userId: uid
          });
        } else {
          clearInterval(reEmitIntervalRef.current);
          reEmitIntervalRef.current = null;
        }
      }, 2000);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing after 1.5s of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      if (socket && conversationId) {
        socket.emit('stopTyping', {
          conversationId,
          userId: uid
        });
      }
      isTypingRef.current = false;

      // Clear re-emit interval when stopping
      if (reEmitIntervalRef.current) {
        clearInterval(reEmitIntervalRef.current);
        reEmitIntervalRef.current = null;
      }
    }, 1500);
  };

  const handleCalendarClick = () => {
    // Dual-mode logic
    if (message.trim()) {
      // Has text → Open schedule picker
      setShowScheduledList(false);
      setShowSchedulePopover(!showSchedulePopover);
    } else {
      // Empty → Open scheduled messages list
      setShowSchedulePopover(false);
      setShowScheduledList(!showScheduledList);
    }
  };

  const handleEditScheduledMessage = (messageText, scheduledDate) => {
    // Load message text into input
    setMessage(messageText);

    // Close list and open picker with the date
    setShowScheduledList(false);
    setShowSchedulePopover(true);

    // Input will be populated automatically via setMessage
  };

  const handleNewMessageClick = () => {
    // Close the list
    setShowScheduledList(false);

    // Focus the input field
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  };

  const handleScheduleMessage = async (scheduledDate) => {
    try {
      // Get ENCRYPTED AES key from activeChat or chatConversationId
      let encryptedAESKey = activeChat?.AESKeyOfUser || chatConversationId?.AESKeyOfUser;

      if (!encryptedAESKey) {
        toast.error("Unable to encrypt message. Please try again.");
        return;
      }

      // CRITICAL: Decrypt the AES key using user's private key (same as regular messages)
      const { decryptText } = await import("../../../../Components/EncryptDecryptMessage/EncryptDecryptMessage");
      const secretKey = await decryptText(encryptedAESKey, userInfo?.encryptedPrivateKey);

      if (!secretKey) {
        toast.error("Unable to encrypt message. Please try again.");
        return;
      }

      // Encrypt the message with the DECRYPTED secret key
      const { iv, encryptedData } = await encrypt(message, secretKey);

      // Prepare data for API
      const data = {
        cipherText: encryptedData,
        iv: iv,
        date: scheduledDate.toISOString(),
        conversationId: chatConversationId._id || chatConversationId,
        isLink: /https?:\/\/[^\s/$.?#].[^\s]*/i.test(message),
        messageId: uuidv4(),
      };

      // Call API to schedule message
      const response = await postApiCaller("messages/schedule", data);

      if (response?.status === 200 || response?.status === 201) {
        // Clear input
        setMessage("");

        // Close popover
        setShowSchedulePopover(false);

        // Show success toast
        const formatDate = (date) => {
          const now = new Date();
          const tomorrow = new Date(now);
          tomorrow.setDate(tomorrow.getDate() + 1);

          const isSameDay = (d1, d2) => {
            return d1.getFullYear() === d2.getFullYear() &&
              d1.getMonth() === d2.getMonth() &&
              d1.getDate() === d2.getDate();
          };

          const timeStr = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });

          if (isSameDay(date, now)) {
            return `Today ${timeStr}`;
          } else if (isSameDay(date, tomorrow)) {
            return `Tomorrow ${timeStr}`;
          } else {
            return date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
          }
        };

        toast.success(`Message scheduled for ${formatDate(scheduledDate)}`);
      } else {
        toast.error(response?.response?.data?.message || "Failed to schedule message");
      }
    } catch (error) {
      toast.error(`Error scheduling message: ${error.message} `);
    }
  };

  const handleFileUpload = async (event) => {
    if (event.target.files.length > 0) {
      if (previewFiles.length + event.target.files.length > 10) {
        toast.error("Maximum 10 files can be uploaded at a time");
        return;
      }

      // Check file sizes before compression
      const validFiles = Array.from(event.target.files).filter((file) => {
        if (file.size > 2 * 1024 * 1024 * 1024) {
          // 2GB in bytes
          toast.error(`The ${file.name} file is too large.Limit: 2 GB`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) {
        return;
      }

      const compressedFiles = await compressFile(validFiles);
      setPreviewFiles((prevFiles) => [...prevFiles, ...compressedFiles]);
      setShowAddMenu(false);
    }
  };

  const handleSubmit = async () => {
    if (isRecording) {
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });

        if (!ffmpeg.loaded) {
          await ffmpeg.load();
        }

        const inputFile = "voice_message.webm";
        const outputFile = "voice_message.m4a";

        // Manually convert Blob to Uint8Array
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        await ffmpeg.writeFile(inputFile, uint8Array);
        await ffmpeg.exec([
          "-i",
          inputFile,
          "-c:a",
          "aac",
          "-b:a",
          "128k",
          outputFile,
        ]);
        const data = await ffmpeg.readFile(outputFile);

        const m4aBlob = new Blob([data], { type: "audio/m4a" });
        const file = new File([m4aBlob], outputFile, {
          type: "audio/m4a",
          lastModified: Date.now(),
        });
        setShowAddMenu(false);
        sendFilesInMessage([file]);
        stream.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
      };
      stopRecording();
      return;
    }
    const trimmed = message.trim();
    if (trimmed === "" && previewFiles.length === 0) return;
    if (trimmed !== "" && previewFiles.length === 0) {
      sendMessage(trimmed);
      setMessage("");
    }
    if (previewFiles.length > 0) {
      sendFilesInMessage(previewFiles, trimmed);
      setPreviewFiles([]); // Clear preview after sending
    }
    setPreventSubmit(false); // reset flag after submit
  };

  const startRecording = async () => {
    try {
      // Clean up any existing recording first
      cleanupRecording();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setStream(stream);
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start(1000); // Collect data every second

      // Analyser setup
      audioCtxRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      analyserRef.current = audioCtxRef.current.createAnalyser();
      sourceRef.current = audioCtxRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);

      analyserRef.current.fftSize = 64;
      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

      // Start the timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);

      setIsRecording(true);
      setIsPaused(false);
    } catch (error) {
      console.error("Error starting recording:", error);
      cleanupRecording();
    }
  };

  const stopRecording = () => {
    setIsRecording(false);
    setIsPaused(false);
    if (
      mediaRecorderRef.current &&
      (mediaRecorderRef.current.state === "recording" ||
        mediaRecorderRef.current.state === "paused")
    ) {
      mediaRecorderRef.current.stop();
    }

    // Stop the timer
    clearInterval(timerRef.current);
    timerRef.current = null;
    setRecordingTime(0);
  };

  const pauseRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearInterval(timerRef.current);
    }
  };
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showEmojiPicker]);
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setShowAddMenu(false);
      }
    };

    if (showAddMenu) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showAddMenu]);

  const resumeRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    }
  };

  // Add a function to remove a file from the preview
  const removePreviewFile = (index) => {
    setPreviewFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handlePaste = (event) => {
    const file = event.clipboardData.files[0];
    if (file) {
      setPreviewFiles([...previewFiles, file]);
    }
  };

  // Add cleanup function
  const cleanupRecording = () => {
    if (isRecording) {
      stopRecording();
    }
    setIsRecording(false);
    setIsPaused(false);
    clearInterval(timerRef.current);
    timerRef.current = null;
    audioChunksRef.current = [];
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current = null;
    }
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setRecordingTime(0);
  };

  return (
    <div className="w-full mb-6 mx-auto max-w-4xl px-4 relative">
      {/* Add a section to display preview files */}
      {previewFiles.length > 0 && (
        <>
          <div className="flex justify-end pb-2">
            <button
              className="py-1 bg-[var(--primary-color)] px-2 text-xs text-white rounded-full"
              onClick={() => setPreviewFiles([])}
            >
              Remove all
            </button>
          </div>
          <div className="flex justify-start gap-2 mb-3 w-full overflow-auto">
            {previewFiles.map((file, index) => (
              <div key={index} className="relative justify-self-center">
                {categorizeFile(file) == "image" ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    className="w-24 h-24 rounded-xl cursor-pointer"
                    onClick={() =>
                      window.open(URL.createObjectURL(file), "_blank")
                    }
                  />
                ) : (
                  <div className="items-center gap-2">
                    <div className="flex items-center gap-2">
                      {categorizeFile(file) == "document" && (
                        <FileIcon url={file.name} />
                      )}
                      {categorizeFile(file) == "audio" && (
                        <FaFileAudio className="size-20 text-red-500" />
                      )}
                      {categorizeFile(file) == "video" && (
                        <FaFileVideo className="size-20 text-purple-500" />
                      )}
                    </div>
                    <span className="text-sm">
                      {(() => {
                        const nameParts = file.name.split(".");
                        const extension = nameParts.pop(); // get file extension
                        const baseName = nameParts.join("."); // in case filename contains multiple dots
                        const shortName =
                          baseName.length > 10
                            ? baseName.slice(0, 5)
                            : baseName;
                        return `${shortName}.${extension} `;
                      })()}
                    </span>
                  </div>
                )}
                <button
                  onClick={() => removePreviewFile(index)}
                  className="absolute top-0 right-0"
                >
                  <IoIosCloseCircle className="w-6 h-6" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
      <form className="flex w-full items-center gap-3 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border border-white/40 dark:border-slate-600/40 shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-full px-4 py-2 relative z-10">
        {/* Add Icon (File Upload) */}
        {!isRecording ? (
          <>
            <input
              id="file-upload"
              name="file-upload"
              type="file"
              className="hidden"
              multiple
              maxLength={2147483648}
              value=""
              accept=".doc,.docx,.pdf,.txt,.xls,.xlsx,.ppt,.pptx,.csv,.ttf,.otf,.ttc,.mp3,.wav,.m4a,.mp4,.mov,.avi,.wmv"
              onChange={handleFileUpload}
            />
            <input
              id="image-upload"
              name="image-upload"
              type="file"
              className="hidden"
              accept=".jpg,.png"
              multiple
              value=""
              onChange={handleFileUpload}
            />

            <div className="relative flex items-center">
              {showAddMenu ? (
                <label
                  className="p-2 text-gray-500 dark:text-zinc-300 pl-0 hover:text-gray-900 dark:hover:text-zinc-100 cursor-pointer"
                  onClick={() => setShowAddMenu(false)}
                >
                  <IoMdClose className="w-6 h-6" />
                </label>
              ) : (
                <label
                  className="p-2 text-gray-500 dark:text-zinc-300 pl-0 hover:text-gray-900 dark:hover:text-zinc-100 cursor-pointer"
                  onClick={() => setShowAddMenu(true)}
                >
                  <IoMdAdd className="w-7 h-7" style={{ strokeWidth: 2 }} />
                </label>
              )}

              {showAddMenu && (
                <div
                  ref={addMenuRef}
                  className="absolute bottom-15.5 left-[-32px] right-0 z-[100] bg-white dark:bg-slate-900 rounded-lg border-b rounded-l-none rounded-br-none p-2 py-6  min-w-[400px]"
                  style={{ pointerEvents: 'auto' }}
                >
                  <ul className="grid grid-cols-3 gap-6">
                    <li className="flex flex-col w-full">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddMenu(false);
                          startRecording();
                        }}
                        className="flex flex-col items-center cursor-pointer w-full h-full border-0 bg-transparent p-0 relative"
                      >
                        <div className="min-w-[40px] min-h-[40px] flex flex-col items-center justify-center gap-1 border border-[#D7D7D733] dark:border-slate-700 bg-[#FCFBFF] dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 p-3 rounded-xl w-full pointer-events-none">
                          <HiOutlineMicrophone className="text-[#E67240] w-5 h-5" />
                        </div>
                        <span className="text-xs text-zinc-900 dark:text-gray-200 pt-2 font-medium pointer-events-none">
                          Record
                        </span>
                      </button>
                    </li>

                    <li className="flex flex-col w-full">
                      <label
                        htmlFor="image-upload"
                        className="flex flex-col items-center cursor-pointer w-full h-full relative"
                      >
                        <div className="min-w-[40px] min-h-[40px] flex flex-col items-center justify-center gap-1 border border-[#D7D7D733] dark:border-slate-700 bg-[#FCFBFF] dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 p-3 rounded-xl w-full pointer-events-none">
                          <TfiGallery className="text-[#E63939] w-5 h-5" />
                        </div>
                        <span className="text-xs text-zinc-900 dark:text-gray-200 pt-2 font-medium pointer-events-none">
                          Gallery
                        </span>
                      </label>
                    </li>

                    <li className="flex flex-col w-full">
                      <label
                        htmlFor="file-upload"
                        className="flex flex-col items-center cursor-pointer w-full h-full relative"
                      >
                        <div className="min-w-[40px] min-h-[40px] flex flex-col items-center justify-center gap-1 border border-[#D7D7D733] dark:border-slate-700 bg-[#FCFBFF] dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 p-3 rounded-xl w-full pointer-events-none">
                          <IoDocumentTextOutline className="text-[#038FCE] w-5 h-5" />
                        </div>
                        <span className="text-xs text-zinc-900 dark:text-gray-200 pt-2 font-medium pointer-events-none">
                          Documents
                        </span>
                      </label>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Input Container */}
            <div className="flex w-full items-center rounded-lg border-none py-1 p-2 relative bg-transparent">
              {/* Emoji Icon */}
              <button
                type="button"
                className="p-2 text-gray-500 dark:text-zinc-300 hover:text-gray-900 dark:hover:text-zinc-100"
                onClick={() => setShowEmojiPicker((prev) => !prev)}
              >
                <FaSmile className="w-6 h-6" style={{ strokeWidth: 1.5 }} />
              </button>

              {/* Emoji Picker */}
              <div
                ref={emojiPickerRef}
                className={`${showEmojiPicker ? "block" : "hidden"
                  } absolute bottom - 14 left - 0 z - 10 bg - white shadow - lg rounded - md`}
              >
                <EmojiPicker theme={theme} onEmojiClick={handleEmojiClick} />
              </div>

              {/* Textarea */}
              <textarea
                id="chat"
                ref={textareaRef}
                rows="1"
                className="block mx-1 p-2 w-full text-sm text-zinc-800 dark:text-zinc-100 bg-transparent outline-none resize-none overflow-hidden placeholder:text-zinc-500 dark:placeholder:text-zinc-400"
                placeholder="Type a message..."
                value={message}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
              />
            </div>

            {/* Calendar Icon - Always active, dual-mode */}
            <button
              type="button"
              className="p-2 text-gray-600 dark:text-zinc-300 hover:text-gray-900 dark:hover:text-zinc-100 cursor-pointer transition-all duration-200"
              onClick={handleCalendarClick}
            >
              <LuCalendarPlus2 className="w-6 h-6" />
            </button>
            {previewFiles.length !== 0 && (
              <button
                type="button"
                onClick={handleSubmit}
                className="text-[#7104F8]"
              >
                <IoSendSharp className="size-7" />
              </button>
            )}
            {/* Microphone/Send Icon - Grouped inside capsule */}
            {previewFiles.length <= 0 && (
              <>
                {message.trim() || isRecording ? (
                  <button
                    onClick={handleSubmit}
                    type="button"
                    className="p-3 text-white bg-indigo-600 rounded-full shadow-lg hover:scale-105 transition-all duration-200"
                  >
                    <MdSend className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMenu(false);
                      startRecording();
                    }}
                    className="p-3 text-zinc-400 bg-transparent rounded-full hover:bg-zinc-100 transition-all duration-200"
                  >
                    <FaMicrophone className="w-5 h-5" />
                  </button>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <div className="flex-1 gap-5 flex justify-between items-center">
              <button
                type="button"
                onClick={stopRecording}
                className="text-red-600"
              >
                <BiTrash className="size-7" />
              </button>
              <div className="flex bg-[#8d36f9] p-2 rounded-md">
                <button
                  type="button"
                  onClick={isPaused ? resumeRecording : pauseRecording}
                  className="text-white flex items-center justify-center mr-5"
                >
                  {isPaused ? (
                    <FiPlay className="size-5" />
                  ) : (
                    <FiPause className="size-7" />
                  )}
                </button>
                <div className="flex gap-[5px] items-center justify-center overflow-hidden">
                  {[
                    4, 8, 12, 16, 20, 24, 28, 30, 28, 26, 24, 22, 20, 18, 16,
                    14, 12, 10, 8, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26,
                  ].map((h, i) => (
                    <div
                      key={i}
                      className={clsx(
                        "w-[3px] bg-white rounded-full",
                        !isPaused && "animate-wave"
                      )}
                      style={{
                        height: `${h} px`,
                        animationDelay: `${i * 0.1} s`,
                      }}
                    />
                  ))}
                </div>
                <div className="text-white mt-1 ml-5">
                  {Math.floor(recordingTime / 60)}:
                  {recordingTime % 60 < 10 ? "0" : ""}
                  {recordingTime % 60}
                </div>
              </div>
              {/* Send button for voice recording */}
              <button
                type="button"
                onClick={handleSubmit}
                className="p-3 text-white bg-indigo-600 rounded-full shadow-lg hover:scale-105 transition-all duration-200"
              >
                <MdSend className="w-5 h-5" />
              </button>
            </div>
          </>
        )}
      </form>

      {/* Schedule Popover */}
      <SchedulePopover
        isOpen={showSchedulePopover}
        onClose={() => setShowSchedulePopover(false)}
        onSchedule={handleScheduleMessage}
        message={message}
      />

      {/* Scheduled Messages List */}
      <ScheduledMessagesList
        isOpen={showScheduledList}
        onClose={() => setShowScheduledList(false)}
        onNewMessage={handleNewMessageClick}
        onEdit={handleEditScheduledMessage}
        conversationId={chatConversationId?._id || chatConversationId}
        secretKey={getSecretKey}
      />
    </div>
  );
};

export default BottomTypeArea;
